import React, { useState } from 'react';
import { MicIcon, SparklesIcon } from 'lucide-react';
import { PhoneFrame } from './components/PhoneFrame';
import { MyVinUniHome } from './components/MyVinUniHome';
import { LexceFab } from './components/LexceFab';
import { LexceAssistant } from './components/lexce/LexceAssistant';
import { useLexceConversation } from './hooks/useLexceConversation';
import { scenarios } from './data/scenarios';
import { LEXCE_AVATAR, VINUNI_LOGO_HORIZONTAL } from './data/homeScreen';
import type { ScenarioId } from './types/lexce';
interface AppProps {
  /** Scenario to auto-play when the mockup loads. */
  initialScenario?: 'none' | 'directions' | 'lostItem' | 'courses';
  /** Show the “Say Hey Lexce!” nudge next to the mascot button. */
  showWakeWordHint?: boolean;
}
export function App({
  initialScenario = 'none',
  showWakeWordHint = true
}: AppProps) {
  const [open, setOpen] = useState(initialScenario !== 'none');
  const {
    turns,
    status,
    activeId,
    start,
    reset
  } = useLexceConversation(initialScenario as ScenarioId);
  const launch = (id: Exclude<ScenarioId, 'none'>) => {
    setOpen(true);
    start(id);
  };
  return <div className="min-h-full w-full bg-vin-mist" data-id="element-0" data-mp="3mk83wrqn3zad">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-10 px-6 py-12 lg:flex-row lg:items-start lg:justify-between lg:gap-16 lg:py-20" data-id="element-1" data-mp="3rcj6k2crlz6m">
        <div className="max-w-md" data-id="element-2" data-mp="5ggxfcks0yi">
          <img src={VINUNI_LOGO_HORIZONTAL} alt="VinUniversity" className="h-7 w-auto" data-id="element-3" data-mp="2zl3bdjx5744s" />
          <div className="mt-8 flex items-center gap-4" data-id="element-4" data-mp="2zng32ospcsv1">
            <img src={LEXCE_AVATAR} alt="" className="h-16 w-16 rounded-2xl object-cover" data-id="element-5" data-mp="33ngee0ki8nxv" />
            <div data-id="element-6" data-mp="38kqafz2efxuo">
              <h1 className="text-[34px] font-extrabold leading-9 tracking-tight text-vin-primary" data-id="element-7" data-mp="38k6w0sbyjrle">
                Hey Lexce!
              </h1>
              <p className="text-[13px] font-medium text-vin-secondary" data-id="element-8" data-mp="nvsup6ui4rx0">The voice assistant inside My VinUni</p>
            </div>
          </div>
          <p className="mt-5 text-[15px] leading-6 text-vin-muted" data-id="element-9" data-mp="stcc2fmhxkt6">
            Students tap the Lexce mascot — or say “Hey Lexce!” hands-free — to ask anything about campus. Lexce
            answers first, then finishes the task for them: opening directions, filing a report with CSD, or
            drafting a registration plan.
          </p>

          <div className="mt-8" data-id="element-10" data-mp="85l12q6r2w8o">
            <h2 className="text-[13px] font-semibold text-vin-ink" data-id="element-11" data-mp="86mfn4mz60v5">Play a voice request</h2>
            <ul className="mt-3 space-y-2.5" data-id="element-12" data-mp="3gklb8561bk84">
              {scenarios.map(scenario => {
              const isActive = activeId === scenario.id;
              return <li key={scenario.id} data-id="element-13" data-mp="3qaxt1pmgszak">
                    <button type="button" onClick={() => launch(scenario.id)} aria-pressed={isActive} className={`flex w-full items-start gap-3 rounded-2xl border bg-white p-4 text-left transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vin-tertiary ${isActive ? 'border-vin-primary ring-1 ring-vin-primary' : 'border-vin-line hover:border-vin-tertiary'}`} data-id="element-14" data-mp="3qbzfeq8xl87l">
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isActive ? 'bg-vin-secondary text-white' : 'bg-vin-mist text-vin-primary'}`} data-id="element-15" data-mp="13eevr8yvu4a6">
                        <MicIcon className="h-4 w-4" aria-hidden="true" data-id="element-16" data-mp="1i3kt0re5tkff" />
                      </span>
                      <span className="min-w-0" data-id="element-17" data-mp="1m565ibms7wp3">
                        <span className="block text-[14px] font-semibold text-vin-ink" data-id="element-18" data-mp="1m3v2ie15usz1">{scenario.label}</span>
                        <span className="mt-1 block text-[12px] italic leading-4 text-vin-muted" data-id="element-19" data-mp="6n4a2o3eclnt">
                          “{scenario.utterance}”
                        </span>
                      </span>
                    </button>
                  </li>;
            })}
            </ul>
            <p className="mt-4 flex items-center gap-2 text-[12px] text-vin-tertiary" data-id="element-20" data-mp="m549is06tpru">
              <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-21" data-mp="h7vmoe43gxk9" />
              Every answer ends with an action Lexce can carry out for the student.
            </p>
          </div>
        </div>

        <PhoneFrame data-id="element-22" data-mp="19vhszu4pigat">
          <MyVinUniHome data-id="element-23" data-mp="19tx4om8zjtle" />
          <LexceFab onOpen={() => setOpen(true)} hint={showWakeWordHint && !open} data-id="element-24" data-mp="14x6j5jeew5sl" />
          <LexceAssistant open={open} turns={turns} status={status} activeId={activeId} onClose={() => setOpen(false)} onStart={start} onReset={reset} data-id="element-25" data-mp="zxtuqu7w6qnw" />
        </PhoneFrame>
      </div>
    </div>;
}